import { useMemo } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import type { CategorySection } from "src/http/api-types";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { GenericCategoryGridWrapper } from "src/website-apps/shared/components/EitherTlcGridsOrCarousels/GenericCategoryGridWrapper";
import { renderWithResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/util";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useCarouselDimensions } from "src/website-apps/shared/hooks/useCarouselDimensions";

interface Props {
  categoryId: number | undefined;
  categoryName: string | undefined;
  popularCategories: CategorySection;
}

/**
 * Top Performers carousel rendered inside BigImmersiveHeaderV2 for
 * non-immersive category-grouping pages (e.g. Broadway Shows, Alternative Music).
 * Remounts when location or date filters change so CategoryGrid refetches.
 */
export const CategoryTopPerformersCarousel = ({ categoryId, categoryName, popularCategories }: Props) => {
  const topPerformersContent = useContent("cid$#3383376#"); // "Top Performers"
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const carouselDim = useCarouselDimensions();
  const isBaseCardRefactor =
    useSimpleFeatureTernary(FeatureSetEnum.UseGridCardBaseComponentRefactor, false, true) ?? false;
  const [gridsState] = useEventsGridContext();
  const { location, dates } = gridsState.filters;
  const filterKey = `${location?.lt ?? ""},${location?.lg ?? ""},${dates?.dates?.from ?? ""},${dates?.dates?.to ?? ""}`;

  const carouselRenderView = useMemo(
    () =>
      renderWithResponsiveCarousel({
        size: carouselDim.carouselSize,
        hidePaginationButtons: isMobile,
        gapPx: carouselDim.gapPx,
        useFlex: isBaseCardRefactor,
        analyticsCarouselId: CarouselId.TopPerformers,
        analyticsItemType: CarouselItemType.Category,
        analyticsCategoryId: categoryId,
      }),
    [carouselDim, isMobile, isBaseCardRefactor, categoryId],
  );

  return (
    <GenericCategoryGridWrapper
      key={filterKey}
      categoryId={categoryId}
      carouselName="category_popular_performers"
      data={popularCategories}
      dedupeCategoryIds={[]}
      removeMargin
      renderView={carouselRenderView}
      text={topPerformersContent}
      exploreText={categoryName}
      textOnCard
      whiteText
      shouldRefreshOnFilterChange
      filterDates={dates}
    />
  );
};
